import { z } from 'zod';
import { supabase } from '@/lib/supabase/client';
import type { Tables, TablesUpdate } from '@/lib/supabase/types';

export type Publication = Tables<'publications'>;

export const publicationKinds = ['article', 'essay', 'book', 'series'] as const;
export type PublicationKind = (typeof publicationKinds)[number];

export const visibilityOptions = ['public', 'unlisted', 'private'] as const;
export type Visibility = (typeof visibilityOptions)[number];

export const slugSchema = z
  .string()
  .min(2, 'Slug must be at least 2 characters')
  .max(100, 'Slug is too long')
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers, and hyphens only');

export const publicationSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title is too long'),
  slug: slugSchema,
  kind: z.enum(publicationKinds, { errorMap: () => ({ message: 'Select a kind' }) }),
  visibility: z.enum(visibilityOptions),
});

export type PublicationInput = z.infer<typeof publicationSchema>;

export function titleToSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 100);
}

export async function getPublicationsByAuthor(authorId: string): Promise<Publication[]> {
  const { data, error } = await supabase
    .from('publications')
    .select('*')
    .eq('author_id', authorId)
    .is('deleted_at', null)
    .in('visibility', ['public', 'unlisted'])
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getMyPublications(): Promise<Publication[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('publications')
    .select('*')
    .eq('author_id', session.user.id)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getPublication(id: string): Promise<Publication> {
  const { data, error } = await supabase
    .from('publications')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

export async function createPublication(input: PublicationInput): Promise<Publication> {
  publicationSchema.parse(input);
  const { data, error } = await supabase
    .from('publications')
    .insert({ title: input.title, slug: input.slug, kind: input.kind, visibility: input.visibility })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updatePublication(
  id: string,
  input: Partial<PublicationInput>,
): Promise<Publication> {
  publicationSchema.partial().parse(input);
  const update: TablesUpdate<'publications'> = {};
  if (input.title !== undefined) update.title = input.title;
  if (input.slug !== undefined) update.slug = input.slug;
  if (input.kind !== undefined) update.kind = input.kind;
  if (input.visibility !== undefined) update.visibility = input.visibility;
  const { data, error } = await supabase
    .from('publications')
    .update(update)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function archivePublication(id: string): Promise<void> {
  const { error } = await supabase
    .from('publications')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
